import { useState } from "react";
import { Card } from "./ui/card";
import { Copy, Check, Sparkles } from "lucide-react";

/**
 * A discount code advertised on the home, rendered as the first tile of the
 * catalogue so it reads like an ad slot rather than a footnote.
 *
 * It occupies a normal grid cell — same size as a course card — but drops the
 * catalogue's visual language: dark gradient, oversized code, no price, no
 * "add to cart". Nothing here is clickable except copying the code; the offer
 * only materialises when the buyer types it at checkout.
 *
 * Fields come from GET /public/discount-codes/featured:
 *   { id, code, kind, value, title, text, expires_at }
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const fmtValue = (offer) =>
  offer.kind === "percent"
    ? `${Number(offer.value) % 1 === 0 ? Number(offer.value) : Number(offer.value).toFixed(1)}%`
    : `${Number(offer.value).toFixed(2).replace(".", ",")} €`;

export default function OfferCodeCard({ offer }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(offer.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked (http, permissions): the code is readable anyway */
    }
  };

  return (
    <Card
      className="relative overflow-hidden rounded-lg border-0 p-6 flex flex-col justify-between text-white bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500 shadow-lg shadow-fuchsia-500/25"
      data-testid={`offer-code-card-${offer.id}`}
    >
      {/* Soft light blobs — decorative only. */}
      <div
        className="pointer-events-none absolute -top-16 -right-12 w-52 h-52 rounded-full bg-white/15 blur-2xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-20 -left-10 w-44 h-44 rounded-full bg-white/10 blur-2xl"
        aria-hidden="true"
      />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm">
          <Sparkles className="w-3 h-3" aria-hidden="true" />
          Oferta
        </span>

        <h3 className="font-display text-3xl leading-tight mt-3 drop-shadow-sm">
          {offer.title}
        </h3>

        {offer.text && (
          <p className="text-sm text-white/85 mt-2 leading-snug">{offer.text}</p>
        )}
      </div>

      <div className="relative mt-5">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/70 mb-1.5">
          Usa el código · {fmtValue(offer)} de descuento
        </p>
        <button
          type="button"
          onClick={copy}
          title="Copiar el código"
          className="group w-full flex items-center justify-between gap-3 rounded-lg border-2 border-dashed border-white/50 bg-white/10 px-4 py-3 text-left transition-colors hover:bg-white/20"
          data-testid={`offer-code-copy-${offer.id}`}
        >
          <span className="font-mono-data font-bold text-2xl sm:text-3xl tracking-wider break-all">
            {offer.code}
          </span>
          {copied ? (
            <Check className="w-5 h-5 shrink-0" aria-hidden="true" />
          ) : (
            <Copy className="w-5 h-5 shrink-0 opacity-70 group-hover:opacity-100" aria-hidden="true" />
          )}
          <span className="sr-only">Copiar el código {offer.code}</span>
        </button>

        {offer.expires_at ? (
          <p
            className="text-xs text-white/80 mt-2.5"
            data-testid={`offer-code-validity-${offer.id}`}
          >
            Válido hasta el {fmtDate(offer.expires_at)}
          </p>
        ) : (
          <p className="text-xs text-white/70 mt-2.5">
            Introdúcelo al finalizar la compra.
          </p>
        )}
      </div>
    </Card>
  );
}
